import { useMemo, useState } from 'react';

const initialState = {
  name: '',
  phone: '',
  email: '',
  description: '',
  website_url: '',
};

export default function ContactForm() {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const validation = useMemo(() => ({
    name: form.name.trim().length >= 2,
    phone: /^[0-9+\s-]{8,20}$/.test(form.phone.trim()),
    email: /^\S+@\S+\.\S+$/.test(form.email.trim()),
    description: form.description.trim().length >= 10,
    website_url: form.website_url.trim() === '',
  }), [form]);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setStatus({ state: 'idle', message: '' });
  }

  function validateForm() {
    const nextErrors = {};

    if (!validation.name) nextErrors.name = 'Indtast venligst dit navn.';
    if (!validation.phone) nextErrors.phone = 'Indtast et gyldigt telefonnummer.';
    if (!validation.email) nextErrors.email = 'Indtast en gyldig e-mailadresse.';
    if (!validation.description) nextErrors.description = 'Beskriv venligst dit projekt lidt mere detaljeret.';
    if (!validation.website_url) nextErrors.website_url = 'Formularen kunne ikke sendes.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setStatus({ state: 'idle', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dkwebform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          description: form.description.trim(),
          website_url: form.website_url.trim(),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.message || 'Der opstod en fejl under afsendelsen. Prøv igen om lidt.';
        throw new Error(message);
      }

      setStatus({ state: 'success', message: 'Tak for din besked. Vi vender tilbage hurtigst muligt.' });
      setForm(initialState);
      setErrors({});
    } catch (error) {
      setStatus({ state: 'error', message: error.message || 'Der opstod en fejl under afsendelsen.' });
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass = 'mt-2 w-full border border-brand-gray-200 bg-white px-4 py-3 text-sm text-brand-black outline-none transition-colors focus:border-brand-orange';

  return (
    <form onSubmit={handleSubmit} className="space-y-5 border border-brand-gray-200 bg-white p-6 sm:p-8">
      <div className="sr-only absolute left-[-10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="website_url">Website</label>
        <input
          id="website_url"
          name="website_url"
          type="text"
          value={form.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block text-sm font-semibold uppercase tracking-[0.14em] text-brand-black">
          Navn
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className={fieldClass}
            type="text"
            autoComplete="name"
            required
          />
          {errors.name ? <span className="mt-2 block text-xs font-medium text-red-600">{errors.name}</span> : null}
        </label>

        <label className="block text-sm font-semibold uppercase tracking-[0.14em] text-brand-black">
          Telefonnummer
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className={fieldClass}
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="42 30 79 39"
            required
          />
          {errors.phone ? <span className="mt-2 block text-xs font-medium text-red-600">{errors.phone}</span> : null}
        </label>
      </div>

      <label className="block text-sm font-semibold uppercase tracking-[0.14em] text-brand-black">
        Email
        <input
          name="email"
          value={form.email}
          onChange={handleChange}
          className={fieldClass}
          type="email"
          autoComplete="email"
          required
        />
        {errors.email ? <span className="mt-2 block text-xs font-medium text-red-600">{errors.email}</span> : null}
      </label>

      <label className="block text-sm font-semibold uppercase tracking-[0.14em] text-brand-black">
        Beskrivelse
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          className={`${fieldClass} min-h-40 resize-y`}
          rows={6}
          required
        />
        {errors.description ? <span className="mt-2 block text-xs font-medium text-red-600">{errors.description}</span> : null}
      </label>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center bg-brand-black px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-brand-orange disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? 'Sender...' : 'Send besked'}
        </button>

        <p className="text-xs leading-6 text-brand-gray-500">
          Ved at sende formularen accepterer du, at vi må kontakte dig om din forespørgsel.
        </p>
      </div>

      {status.state !== 'idle' ? (
        <p className={`text-sm font-medium ${status.state === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
          {status.message}
        </p>
      ) : null}
    </form>
  );
}
